import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getLiveClass } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Video, Loader2, AlertCircle } from 'lucide-react';

export default function LiveClassRoom() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [liveClass, setLiveClass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jitsiLoading, setJitsiLoading] = useState(true);
  const [error, setError] = useState(null);
  const jitsiContainerRef = useRef(null);
  const jitsiApiRef = useRef(null);
  const initAttempts = useRef(0);

  useEffect(() => {
    const fetchClass = async () => {
      try {
        const data = await getLiveClass(classId);
        setLiveClass(data);
      } catch (err) {
        setError('No se pudo cargar la clase');
        setLoading(false);
      }
    };
    fetchClass();
  }, [classId]);

  // Auto-join: initialize Jitsi as soon as class data is loaded
  useEffect(() => {
    if (!liveClass) return;
    setLoading(false);

    // Timeout: hide loading after 8s even if event doesn't fire
    const loadingTimeout = setTimeout(() => {
      setJitsiLoading(false);
    }, 8000);

    const initJitsi = () => {
      if (!jitsiContainerRef.current) {
        if (initAttempts.current < 20) {
          initAttempts.current++;
          setTimeout(initJitsi, 300);
        }
        return;
      }

      if (window.JitsiMeetExternalAPI) {
        try {
          if (jitsiApiRef.current) {
            jitsiApiRef.current.dispose();
          }
          const api = new window.JitsiMeetExternalAPI('jitsi.hamburg.ccc.de', {
            roomName: liveClass.room_id,
            width: '100%',
            height: '100%',
            parentNode: jitsiContainerRef.current,
            userInfo: {
              displayName: user?.name || 'Participante'
            },
            configOverwrite: {
              startWithAudioMuted: true,
              startWithVideoMuted: false,
              prejoinPageEnabled: false,
              disableDeepLinking: true,
              enableClosePage: false,
              hideConferenceSubject: false,
              subject: liveClass.title,
              defaultLanguage: 'es'
            },
            interfaceConfigOverwrite: {
              TOOLBAR_BUTTONS: [
                'microphone', 'camera', 'desktop', 'fullscreen',
                'hangup', 'chat', 'raisehand', 'tileview',
                'videoquality', 'filmstrip', 'participants-pane', 'settings',
                'select-background', 'mute-everyone'
              ],
              SHOW_JITSI_WATERMARK: false,
              SHOW_BRAND_WATERMARK: false,
              DEFAULT_BACKGROUND: '#1A1A1A',
              DISABLE_JOIN_LEAVE_NOTIFICATIONS: false,
              MOBILE_APP_PROMO: false,
              HIDE_INVITE_MORE_HEADER: true
            }
          });
          jitsiApiRef.current = api;
          api.addListener('videoConferenceJoined', () => {
            setJitsiLoading(false);
          });
          api.addListener('readyToClose', () => {
            navigate(-1);
          });
        } catch (err) {
          console.error('Jitsi init error:', err);
          setError('Error al iniciar la videoconferencia');
          setJitsiLoading(false);
        }
      } else {
        // Script not loaded yet, retry
        if (initAttempts.current < 30) {
          initAttempts.current++;
          setTimeout(initJitsi, 500);
        } else {
          setError('No se pudo cargar el sistema de videoconferencia. Recarga la pagina.');
          setJitsiLoading(false);
        }
      }
    };

    initJitsi();

    return () => {
      clearTimeout(loadingTimeout);
      if (jitsiApiRef.current) {
        jitsiApiRef.current.dispose();
        jitsiApiRef.current = null;
      }
    };
  }, [liveClass, user, navigate]);

  const handleLeave = () => {
    if (jitsiApiRef.current) {
      jitsiApiRef.current.dispose();
      jitsiApiRef.current = null;
    }
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#1A1A1A] flex items-center justify-center z-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#8B1538] mx-auto mb-4" />
          <p className="text-white/70 text-lg">Cargando clase...</p>
        </div>
      </div>
    );
  }

  if (error && !liveClass) {
    return (
      <div className="fixed inset-0 bg-[#1A1A1A] flex items-center justify-center z-50">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-[#9B2226] mx-auto mb-4" />
          <h2 className="text-white text-xl mb-2">Clase no encontrada</h2>
          <p className="text-white/50 mb-6">{error}</p>
          <Button onClick={() => navigate(-1)} className="bg-[#8B1538] hover:bg-[#70102B] text-white">
            <ArrowLeft className="h-4 w-4 mr-2" /> Volver
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#1A1A1A] flex flex-col z-50" data-testid="live-class-room">
      {/* Minimal header */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#111111] border-b border-white/10 shrink-0">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLeave}
            className="text-white/70 hover:text-white hover:bg-white/10"
            data-testid="leave-class-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Salir
          </Button>
          <div className="h-5 w-px bg-white/20" />
          <div className="flex items-center gap-2">
            <Video className="h-4 w-4 text-[#8B1538]" />
            <span className="text-white font-medium text-sm truncate max-w-[300px]">{liveClass?.title}</span>
          </div>
          {liveClass?.subject_title && (
            <span className="text-white/40 text-xs hidden md:inline">| {liveClass.subject_title}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!jitsiLoading && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#2D6A4F] text-white flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> EN VIVO
            </span>
          )}
        </div>
      </div>

      {/* Full-screen video area */}
      <div className="flex-1 relative">
        <div
          ref={jitsiContainerRef}
          data-testid="jitsi-container"
          className="absolute inset-0"
        />
        {jitsiLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#1A1A1A]">
            <Loader2 className="h-12 w-12 animate-spin text-[#8B1538] mb-4" />
            <p className="text-white/70 text-lg mb-1">Conectando a la clase...</p>
            <p className="text-white/40 text-sm">Preparando camara y microfono</p>
          </div>
        )}
        {error && !jitsiLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#1A1A1A]">
            <AlertCircle className="h-12 w-12 text-[#9B2226] mb-4" />
            <p className="text-white/70 text-lg mb-4">{error}</p>
            <Button onClick={() => window.location.reload()} className="bg-[#8B1538] hover:bg-[#70102B] text-white">
              Reintentar
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
